import { useContext, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import uniq from "lodash/uniq";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { ALERT_TYPE, PASSENGER_TYPE } from "app/constants";
import PaymentPassengersFieldset from "app/pages/Booking/Payment/PaymentPassengersSection/PaymentPassengersFieldset";
import AppGlobalsContext from "app/AppGlobalsContext";
import PropTypes from "prop-types";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { useFormikContext } from "formik";
import IconPaymentDisclaimer from "app/pages/.shared/static/icons/IconPaymentDisclaimer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PASSENGER_TYPE_ID = {
	[PASSENGER_TYPE.CHILD]: "payment.passengers.child",
	[PASSENGER_TYPE.INFANT]: "payment.passengers.infant",
	[PASSENGER_TYPE.ADULT]: "payment.passengers.adult",
};

const PaymentPassengersSection = ({
	bookStatus,
	productUri,
	dateOfBirthRequired,
	showFrequentTravellerCardInput,
	isChildrenAgeRequired,
	showInsurancesSection,
	warningMessage = false,
}) => {
	const { errors, values } = useFormikContext();
	const { resolution } = useContext(AppGlobalsContext);

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });
	const intl = useIntl();

	useEffect(() => {
		if (isElementOnScreen) {
			track("payment_view_information");
		}
	}, [isElementOnScreen]);

	const passengersErrorList = [];

	// Cette fonction est utilisée pour éviter l'erreur "Maximum call stack size exceeded" générée par le composant FormErrorMessages.
	// L'erreur est due à la taille excessive de l'objet _owner généré par le système.
	// L'appel à la fonction clean(errors) dans FormErrorMessages provoque une récursion infinie en raison de la taille de cet
	if (errors?.passengers?.length > 0) {
		errors.passengers.forEach((passengerError, index) => {
			if (passengerError) {
				const passengerType = values.passengers[index].type;
				const errorPassengerID = PASSENGER_TYPE_ID[passengerType];

				const addErrorToList = errorDetail => {
					passengersErrorList.push(
						`${intl.formatMessage(
							{ id: errorPassengerID },
							{ index: index + 1 }
						)}: ${intl.formatMessage(errorDetail)}`
					);
				};

				for (const key of Object.keys(passengerError)) {
					const errorDetail = passengerError[key];

					if (
						typeof errorDetail === "object" &&
						errorDetail !== null &&
						!errorDetail.id
					) {
						// C'est un objet d'erreur imbriqué, on parcourt ses clés
						Object.values(errorDetail).forEach(addErrorToList);
					} else {
						// C'est une erreur simple, on l'ajoute à la liste
						addErrorToList(errorDetail);
					}
				}
			}
		});
	}
	return (
		<>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.XL3}
				variantMD={TYPOGRAPHY_VARIANTS.XL4}
				isBold
				className="payment__title"
				component="h2"
			>
				<FormattedMessage id="payment.passengers.title" />
			</Typography>
			<div className="payment__content">
				<div className="payment__disclaimer">
					<IconPaymentDisclaimer />
					<div className="payment__disclaimer-content">
						<FormattedMessage id="payment.disclamer.1" tagName="div" />
						<FormattedMessage id="payment.disclamer.2" tagName="div" />
					</div>
				</div>
				{uniq(passengersErrorList).length > 0 && (
					<AlertMessage
						alertType={ALERT_TYPE.ERROR}
						message={<FormattedMessage id="error.booking.payment.fields.required" />}
						errors={uniq(passengersErrorList)}
					/>
				)}
				{warningMessage && (
					<AlertMessage
						alertType={ALERT_TYPE.INFO}
						message={<FormattedMessage id="general.warning.adult.destination.usa" />}
					/>
				)}
				<div className="payment__passengers">
					<PaymentPassengersFieldset
						bookStatus={bookStatus}
						resolution={resolution}
						productUri={productUri}
						dateOfBirthRequired={dateOfBirthRequired}
						showFrequentTravellerCardInput={showFrequentTravellerCardInput}
						isChildrenAgeRequired={isChildrenAgeRequired}
					/>
				</div>
				{!showInsurancesSection && (
					<div className="payment__insurance-note">
						<FormattedMessage id="payment.passengers.insurance.note" />
					</div>
				)}
			</div>
		</>
	);
};

PaymentPassengersSection.propTypes = {
	bookStatus: PropTypes.string,
	productUri: PropTypes.string,
	dateOfBirthRequired: PropTypes.bool,
	showFrequentTravellerCardInput: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	showInsurancesSection: PropTypes.bool,
	warningMessage: PropTypes.bool,
};

export default PaymentPassengersSection;
